import { ExInput, ExTextarea } from '@boomi/exosphere';
import type { IdpType } from './identityProviders';
import styles from '../shared/Form.module.css';

export interface FormValues {
    name: string;
    allowedAudience: string;
    clientId: string;
    clientSecret: string;
    wellKnownUrl: string;
    entityId: string;
    metadata: string;
}

export const EMPTY_FORM_VALUES: FormValues = {
    name: '',
    allowedAudience: '',
    clientId: '',
    clientSecret: '',
    wellKnownUrl: '',
    entityId: '',
    metadata: '',
};

interface IdentityProviderFormFieldsProps {
    type: IdpType;
    values: FormValues;
    onChange: (patch: Partial<FormValues>) => void;
}

const IdentityProviderFormFields = ({
    type,
    values,
    onChange,
}: IdentityProviderFormFieldsProps) => (
    <div className={styles.fields}>
        <ExInput
            label="Name"
            type="text"
            value={values.name}
            onInput={(e) => onChange({ name: (e.target as HTMLInputElement).value })}
            placeholder="e.g. Okta"
            required
        />

        <ExInput
            label="Allowed Audience"
            type="text"
            value={values.allowedAudience}
            onInput={(e) => onChange({ allowedAudience: (e.target as HTMLInputElement).value })}
            placeholder="e.g. https://your-domain.okta.com"
            helpText="The audience value expected in incoming tokens from this provider."
            required
        />

        {type === 'OAUTH' && (
            <>
                <ExInput
                    label="Well-Known URL"
                    type="url"
                    value={values.wellKnownUrl}
                    onInput={(e) =>
                        onChange({ wellKnownUrl: (e.target as HTMLInputElement).value })
                    }
                    placeholder="https://your-domain.okta.com/.well-known/openid-configuration"
                    helpText="The OpenID Connect discovery document URL for this provider."
                    required
                />

                <ExInput
                    label="Client ID"
                    type="text"
                    value={values.clientId}
                    onInput={(e) => onChange({ clientId: (e.target as HTMLInputElement).value })}
                    required
                />

                <ExInput
                    label="Client Secret"
                    type="password"
                    togglePassword
                    value={values.clientSecret}
                    onInput={(e) =>
                        onChange({ clientSecret: (e.target as HTMLInputElement).value })
                    }
                    required
                />
            </>
        )}

        {type === 'SAML' && (
            <>
                <ExInput
                    label="Entity ID"
                    type="text"
                    value={values.entityId}
                    onInput={(e) => onChange({ entityId: (e.target as HTMLInputElement).value })}
                    placeholder="https://your-idp.example.com/entity"
                    helpText="The unique identifier for the identity provider in SAML metadata."
                    required
                />

                <ExTextarea
                    dangerouslyUseHTML
                    label="Metadata XML"
                    value={values.metadata}
                    onInput={(e) => onChange({ metadata: (e.target as HTMLTextAreaElement).value })}
                    placeholder="Paste your IdP SAML metadata XML here"
                    rows={8}
                    required
                />
            </>
        )}
    </div>
);

export default IdentityProviderFormFields;
